/* @jsx h */
import { defineComponent, h } from "vue";
import type { ProductNutritionalInformation } from "@lemmonhq/types";

export interface NutritionalLabelProps {
  nutrition: ProductNutritionalInformation;
  variant?: "full" | "compact";
}

const formatValue = (value: number | undefined, suffix = "") => {
  if (typeof value !== "number" || !Number.isFinite(value)) return "—";
  return `${value}${suffix}`;
};

export const NutritionalLabel = defineComponent<NutritionalLabelProps>({
  name: "NutritionalLabel",
  props: ["nutrition", "variant"],
  setup(props) {
    const variant = props.variant || "full";
    const nutrition = props.nutrition;

    if (variant === "compact") {
      return () => (
        <div class="bg-white text-black border-2 border-black rounded-lg font-mono text-xs shadow-sm leading-tight w-full">
          <div class="px-3 pt-3 pb-2 border-b border-black text-center w-full">
            <div class="text-sm font-extrabold uppercase tracking-wide">
              Nutrition Facts
            </div>
            <div class="mt-1">
              Serving size {nutrition.servingSize.amount}
              {nutrition.servingSize.unit}
            </div>
            {nutrition.servingSize.servingsPerContainer && (
              <div class="mt-0.5 text-[10px]">
                Servings per container{" "}
                {nutrition.servingSize.servingsPerContainer}
              </div>
            )}
          </div>

          <div class="px-3 py-2 border-b border-black flex items-baseline justify-between">
            <span class="text-sm font-bold uppercase tracking-wide">
              Calories
            </span>
            <span class="text-xl font-black">{nutrition.calories}</span>
          </div>

          <div class="px-3 py-2 space-y-1">
            <div class="flex justify-between">
              <span>Total Fat</span>
              <span>
                {formatValue(nutrition.macronutrients.totalFat.value, " g")}
              </span>
            </div>
            <div class="flex justify-between">
              <span>Total Carbs</span>
              <span>
                {formatValue(
                  nutrition.macronutrients.totalCarbohydrate.value,
                  " g",
                )}
              </span>
            </div>
            <div class="flex justify-between">
              <span>Protein</span>
              <span>
                {formatValue(nutrition.macronutrients.protein.value, " g")}
              </span>
            </div>
            {nutrition.macronutrients.totalCarbohydrate.totalSugars !==
              undefined && (
              <div class="flex justify-between">
                <span>Total Sugars</span>
                <span>
                  {formatValue(
                    nutrition.macronutrients.totalCarbohydrate.totalSugars,
                    " g",
                  )}
                </span>
              </div>
            )}
            {nutrition.macronutrients.sodium && (
              <div class="flex justify-between">
                <span>Sodium</span>
                <span>
                  {formatValue(nutrition.macronutrients.sodium.value, " mg")}
                </span>
              </div>
            )}
          </div>
        </div>
      );
    }

    return () => (
      <div class="bg-white text-black border-4 border-black rounded-lg font-mono ">
        <header class="px-4 pt-4 pb-3 border-b-4 border-black">
          <div class="text-3xl font-extrabold leading-none uppercase tracking-tight">
            Nutrition Facts
          </div>
          <div class="text-xs mt-1">
            Serving size {nutrition.servingSize.amount}
            {nutrition.servingSize.unit}
          </div>
          {nutrition.servingSize.servingsPerContainer && (
            <div class="text-xs">
              Servings per container{" "}
              {nutrition.servingSize.servingsPerContainer}
            </div>
          )}
        </header>

        <section class="px-4 py-3 border-b-4 border-black">
          <div class="flex justify-between items-baseline">
            <span class="text-lg font-bold uppercase tracking-wide">
              Calories
            </span>
            <span class="text-3xl font-black">{nutrition.calories}</span>
          </div>
          {nutrition.energyKj && (
            <div class="text-[11px] mt-1">({nutrition.energyKj} kJ)</div>
          )}
        </section>

        <section class="px-4 py-3 border-b border-black text-xs font-bold flex justify-between">
          <span>% Daily Value*</span>
        </section>

        <section class="px-4 py-3 border-b border-black text-sm space-y-1">
          <div class="flex justify-between">
            <span>
              <strong>Total Fat</strong>{" "}
              {formatValue(nutrition.macronutrients.totalFat.value, " g")}
            </span>
            <span class="font-bold">
              {nutrition.percentDailyValues?.totalFat ?? "—"}%
            </span>
          </div>
          {nutrition.macronutrients.totalFat.saturatedFat !== undefined && (
            <div class="flex justify-between ml-4">
              <span>
                Saturated Fat{" "}
                {formatValue(
                  nutrition.macronutrients.totalFat.saturatedFat,
                  " g",
                )}
              </span>
              <span class="font-bold">
                {nutrition.percentDailyValues?.saturatedFat ?? "—"}%
              </span>
            </div>
          )}
          {nutrition.macronutrients.totalFat.transFat !== undefined && (
            <div class="flex justify-between ml-4">
              <span>
                <em>Trans</em> Fat{" "}
                {formatValue(nutrition.macronutrients.totalFat.transFat, " g")}
              </span>
              <span></span>
            </div>
          )}

          {nutrition.macronutrients.cholesterol && (
            <div class="flex justify-between">
              <span>
                <strong>Cholesterol</strong>{" "}
                {formatValue(nutrition.macronutrients.cholesterol.value, " mg")}
              </span>
              <span class="font-bold">
                {nutrition.percentDailyValues?.cholesterol ?? "—"}%
              </span>
            </div>
          )}

          {nutrition.macronutrients.sodium && (
            <div class="flex justify-between">
              <span>
                <strong>Sodium</strong>{" "}
                {formatValue(nutrition.macronutrients.sodium.value, " mg")}
              </span>
              <span class="font-bold">
                {nutrition.percentDailyValues?.sodium ?? "—"}%
              </span>
            </div>
          )}

          <div class="flex justify-between">
            <span>
              <strong>Total Carbohydrate</strong>{" "}
              {formatValue(
                nutrition.macronutrients.totalCarbohydrate.value,
                " g",
              )}
            </span>
            <span class="font-bold">
              {nutrition.percentDailyValues?.totalCarbohydrate ?? "—"}%
            </span>
          </div>
          {nutrition.macronutrients.totalCarbohydrate.dietaryFiber !==
            undefined && (
            <div class="flex justify-between ml-4">
              <span>
                Dietary Fiber{" "}
                {formatValue(
                  nutrition.macronutrients.totalCarbohydrate.dietaryFiber,
                  " g",
                )}
              </span>
              <span class="font-bold">
                {nutrition.percentDailyValues?.dietaryFiber ?? "—"}%
              </span>
            </div>
          )}
          {nutrition.macronutrients.totalCarbohydrate.totalSugars !==
            undefined && (
            <div class="flex justify-between ml-4">
              <span>
                Total Sugars{" "}
                {formatValue(
                  nutrition.macronutrients.totalCarbohydrate.totalSugars,
                  " g",
                )}
              </span>
              <span></span>
            </div>
          )}
          {nutrition.macronutrients.totalCarbohydrate.addedSugars !==
            undefined && (
            <div class="flex justify-between ml-8">
              <span>
                Includes{" "}
                {formatValue(
                  nutrition.macronutrients.totalCarbohydrate.addedSugars,
                  " g",
                )}{" "}
                Added Sugars
              </span>
              <span></span>
            </div>
          )}

          <div class="flex justify-between">
            <span>
              <strong>Protein</strong>{" "}
              {formatValue(nutrition.macronutrients.protein.value, " g")}
            </span>
            <span class="font-bold">
              {nutrition.percentDailyValues?.protein ?? "—"}%
            </span>
          </div>
        </section>

        {nutrition.micronutrients && (
          <section class="px-4 py-2 border-b border-black text-[11px] space-y-1">
            {Object.entries(nutrition.micronutrients).map(([key, value]) =>
              value !== undefined ? (
                <div class="flex justify-between capitalize" key={key}>
                  <span>{key.replace(/([A-Z])/g, " $1").trim()}</span>
                  <span>{value}</span>
                </div>
              ) : null,
            )}
          </section>
        )}

        <footer class="px-4 py-3 text-[10px] leading-snug">
          * Percent Daily Values are based on a 2,000 calorie diet. Your daily
          values may be higher or lower depending on your calorie needs.
        </footer>
      </div>
    );
  },
});
